<template>
  <div class="lc-amendment-details">
    <div v-if="loading" class="text-center py-6">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-2">Loading LC amendment details...</p>
    </div>

    <div v-else-if="!amendmentData || Object.keys(amendmentData).length === 0" class="text-center py-6">
      <p class="text-grey">No LC amendment data found</p>
    </div>

    <div v-else>
      <div class="section-title">LC Amendment Details</div>

      <table class="info-table">
        <tbody>
        <tr>
          <td class="info-label"><strong>Amendment No:</strong></td>
          <td class="info-value">{{ amendmentData.amendment_no || "N/A" }}</td>
          <td class="info-label"><strong>Amendment Date:</strong></td>
          <td class="info-value">{{ amendmentData.amendment_date ? formatDateView(amendmentData.amendment_date) : "N/A" }}</td>
        </tr>

        <tr>
          <td class="info-label"><strong>Amendment Charge:</strong></td>
          <td class="info-value">{{ formatCurrency(amendmentData.amendment_charge) }}</td>
          <td class="info-label"><strong>Amendment Type(s):</strong></td>
          <td class="info-value">
              <span v-for="(type, index) in amendmentData.amendment_types || []" :key="index" class="chip">
                {{ type }}
              </span>
            <span v-if="!amendmentData.amendment_types || amendmentData.amendment_types.length === 0">N/A</span>
          </td>
        </tr>

        <tr v-if="amendmentData.remarks">
          <td class="info-label"><strong>Remarks:</strong></td>
          <td class="info-value" colspan="3">{{ amendmentData.remarks }}</td>
        </tr>
        </tbody>
      </table>

      <div v-if="amendmentData.lc_changes && amendmentData.lc_changes.length > 0">
        <div class="section-title">LC Field Changes</div>

        <table class="items-table">
          <thead>
          <tr>
            <th style="width: 30%">Field</th>
            <th style="width: 35%">Before</th>
            <th style="width: 35%">After</th>
          </tr>
          </thead>

          <tbody>
          <tr v-for="(change, index) in amendmentData.lc_changes" :key="index">
            <td>{{ String(change.field || "").replace(/_/g, " ") }}</td>
            <td :class="String(change.before) !== String(change.after) ? 'diff-before' : ''">{{ change.before ?? "N/A" }}</td>
            <td :class="String(change.before) !== String(change.after) ? 'diff-after' : ''">{{ change.after ?? "N/A" }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="section-title">LC Details Comparison</div>

      <div class="compare-wrap">
        <div class="compare-column">
          <div class="compare-header before-header">LC BEFORE AMENDMENT</div>

          <table class="compare-table">
            <tbody>
            <tr>
              <td class="compare-label">LC No</td>
              <td :class="amendmentData.lc_before?.lc_no !== amendmentData.lc_after?.lc_no ? 'diff-before' : ''">
                {{ amendmentData.lc_before?.lc_no || "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">LC Status</td>
              <td :class="amendmentData.lc_before?.lc_status !== amendmentData.lc_after?.lc_status ? 'diff-before' : ''">
                {{ amendmentData.lc_before?.lc_status || "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">LC Amount</td>
              <td :class="amendmentData.lc_before?.lc_amount !== amendmentData.lc_after?.lc_amount ? 'diff-before' : ''">
                {{ formatCurrency(amendmentData.lc_before?.lc_amount) }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">LC Open Date</td>
              <td :class="amendmentData.lc_before?.lc_open_date !== amendmentData.lc_after?.lc_open_date ? 'diff-before' : ''">
                {{ amendmentData.lc_before?.lc_open_date ? formatDateView(amendmentData.lc_before.lc_open_date) : "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Expiry Date</td>
              <td :class="amendmentData.lc_before?.expiry_date !== amendmentData.lc_after?.expiry_date ? 'diff-before' : ''">
                {{ amendmentData.lc_before?.expiry_date ? formatDateView(amendmentData.lc_before.expiry_date) : "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Last Shipment Date</td>
              <td :class="amendmentData.lc_before?.last_shipment_date !== amendmentData.lc_after?.last_shipment_date ? 'diff-before' : ''">
                {{ amendmentData.lc_before?.last_shipment_date ? formatDateView(amendmentData.lc_before.last_shipment_date) : "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Presentation Period (Days)</td>
              <td :class="amendmentData.lc_before?.presentation_period !== amendmentData.lc_after?.presentation_period ? 'diff-before' : ''">
                {{ amendmentData.lc_before?.presentation_period ?? "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Margin %</td>
              <td :class="amendmentData.lc_before?.margin_percent !== amendmentData.lc_after?.margin_percent ? 'diff-before' : ''">
                {{ amendmentData.lc_before?.margin_percent ?? "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Margin Amount</td>
              <td :class="amendmentData.lc_before?.margin_amount !== amendmentData.lc_after?.margin_amount ? 'diff-before' : ''">
                {{ formatCurrency(amendmentData.lc_before?.margin_amount) }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Bank Charge</td>
              <td :class="amendmentData.lc_before?.bank_charge !== amendmentData.lc_after?.bank_charge ? 'diff-before' : ''">
                {{ formatCurrency(amendmentData.lc_before?.bank_charge) }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Swift Charge</td>
              <td :class="amendmentData.lc_before?.swift_charge !== amendmentData.lc_after?.swift_charge ? 'diff-before' : ''">
                {{ formatCurrency(amendmentData.lc_before?.swift_charge) }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Insurance</td>
              <td :class="amendmentData.lc_before?.insurance !== amendmentData.lc_after?.insurance ? 'diff-before' : ''">
                {{ formatCurrency(amendmentData.lc_before?.insurance) }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Approval Status</td>
              <td :class="amendmentData.lc_before?.approval_status !== amendmentData.lc_after?.approval_status ? 'diff-before' : ''">
                {{ amendmentData.lc_before?.approval_status || "N/A" }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="compare-column after-column">
          <div class="compare-header after-header">LC AFTER AMENDMENT</div>

          <table class="compare-table">
            <tbody>
            <tr>
              <td class="compare-label">LC No</td>
              <td :class="amendmentData.lc_before?.lc_no !== amendmentData.lc_after?.lc_no ? 'diff-after' : ''">
                {{ amendmentData.lc_after?.lc_no || "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">LC Status</td>
              <td :class="amendmentData.lc_before?.lc_status !== amendmentData.lc_after?.lc_status ? 'diff-after' : ''">
                {{ amendmentData.lc_after?.lc_status || "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">LC Amount</td>
              <td :class="amendmentData.lc_before?.lc_amount !== amendmentData.lc_after?.lc_amount ? 'diff-after' : ''">
                {{ formatCurrency(amendmentData.lc_after?.lc_amount) }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">LC Open Date</td>
              <td :class="amendmentData.lc_before?.lc_open_date !== amendmentData.lc_after?.lc_open_date ? 'diff-after' : ''">
                {{ amendmentData.lc_after?.lc_open_date ? formatDateView(amendmentData.lc_after.lc_open_date) : "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Expiry Date</td>
              <td :class="amendmentData.lc_before?.expiry_date !== amendmentData.lc_after?.expiry_date ? 'diff-after' : ''">
                {{ amendmentData.lc_after?.expiry_date ? formatDateView(amendmentData.lc_after.expiry_date) : "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Last Shipment Date</td>
              <td :class="amendmentData.lc_before?.last_shipment_date !== amendmentData.lc_after?.last_shipment_date ? 'diff-after' : ''">
                {{ amendmentData.lc_after?.last_shipment_date ? formatDateView(amendmentData.lc_after.last_shipment_date) : "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Presentation Period (Days)</td>
              <td :class="amendmentData.lc_before?.presentation_period !== amendmentData.lc_after?.presentation_period ? 'diff-after' : ''">
                {{ amendmentData.lc_after?.presentation_period ?? "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Margin %</td>
              <td :class="amendmentData.lc_before?.margin_percent !== amendmentData.lc_after?.margin_percent ? 'diff-after' : ''">
                {{ amendmentData.lc_after?.margin_percent ?? "N/A" }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Margin Amount</td>
              <td :class="amendmentData.lc_before?.margin_amount !== amendmentData.lc_after?.margin_amount ? 'diff-after' : ''">
                {{ formatCurrency(amendmentData.lc_after?.margin_amount) }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Bank Charge</td>
              <td :class="amendmentData.lc_before?.bank_charge !== amendmentData.lc_after?.bank_charge ? 'diff-after' : ''">
                {{ formatCurrency(amendmentData.lc_after?.bank_charge) }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Swift Charge</td>
              <td :class="amendmentData.lc_before?.swift_charge !== amendmentData.lc_after?.swift_charge ? 'diff-after' : ''">
                {{ formatCurrency(amendmentData.lc_after?.swift_charge) }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Insurance</td>
              <td :class="amendmentData.lc_before?.insurance !== amendmentData.lc_after?.insurance ? 'diff-after' : ''">
                {{ formatCurrency(amendmentData.lc_after?.insurance) }}
              </td>
            </tr>

            <tr>
              <td class="compare-label">Approval Status</td>
              <td :class="amendmentData.lc_before?.approval_status !== amendmentData.lc_after?.approval_status ? 'diff-after' : ''">
                {{ amendmentData.lc_after?.approval_status || "N/A" }}
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="(amendmentData.pi_before && amendmentData.pi_before.length > 0) || (amendmentData.pi_after && amendmentData.pi_after.length > 0)">
        <div class="section-title">Proforma Invoice Comparison</div>

        <div v-for="piIndex in Math.max(amendmentData.pi_before?.length || 0, amendmentData.pi_after?.length || 0)" :key="piIndex - 1" class="pi-section">
          <div class="pi-header-bar">
            <span>
              <strong>PI No:</strong>
              {{ amendmentData.pi_after?.[piIndex - 1]?.pi_no || amendmentData.pi_before?.[piIndex - 1]?.pi_no || "N/A" }}
            </span>

            <span>
              <strong>PI Date:</strong>
              {{
                amendmentData.pi_after?.[piIndex - 1]?.pi_date
                    ? formatDateView(amendmentData.pi_after[piIndex - 1].pi_date)
                    : amendmentData.pi_before?.[piIndex - 1]?.pi_date
                        ? formatDateView(amendmentData.pi_before[piIndex - 1].pi_date)
                        : "N/A"
              }}
            </span>

            <span>
              <strong>Currency:</strong>
              {{ amendmentData.pi_after?.[piIndex - 1]?.currency?.code || amendmentData.pi_before?.[piIndex - 1]?.currency?.code || "N/A" }}
            </span>
          </div>

          <div class="pi-details">
            <div class="pi-detail-column">
              <div class="compare-subtitle before-text">BEFORE</div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">PI No:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.pi_no !== amendmentData.pi_after?.[piIndex - 1]?.pi_no ? 'diff-before' : ''">
                  {{ amendmentData.pi_before?.[piIndex - 1]?.pi_no || "N/A" }}
                </span>
              </div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">PI Date:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.pi_date !== amendmentData.pi_after?.[piIndex - 1]?.pi_date ? 'diff-before' : ''">
                  {{ amendmentData.pi_before?.[piIndex - 1]?.pi_date ? formatDateView(amendmentData.pi_before[piIndex - 1].pi_date) : "N/A" }}
                </span>
              </div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">Validity:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.validity !== amendmentData.pi_after?.[piIndex - 1]?.validity ? 'diff-before' : ''">
                  {{ amendmentData.pi_before?.[piIndex - 1]?.validity ? formatDateView(amendmentData.pi_before[piIndex - 1].validity) : "N/A" }}
                </span>
              </div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">Approval Status:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.approval_status !== amendmentData.pi_after?.[piIndex - 1]?.approval_status ? 'diff-before' : ''">
                  {{ amendmentData.pi_before?.[piIndex - 1]?.approval_status || "N/A" }}
                </span>
              </div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">Sub Total:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.sub_total !== amendmentData.pi_after?.[piIndex - 1]?.sub_total ? 'diff-before' : ''">
                  {{ formatCurrency(amendmentData.pi_before?.[piIndex - 1]?.sub_total, amendmentData.pi_before?.[piIndex - 1]?.currency?.code) }}
                </span>
              </div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">Total Amount:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.total_amount !== amendmentData.pi_after?.[piIndex - 1]?.total_amount ? 'diff-before' : ''">
                  {{ formatCurrency(amendmentData.pi_before?.[piIndex - 1]?.total_amount, amendmentData.pi_before?.[piIndex - 1]?.currency?.code) }}
                </span>
              </div>
            </div>

            <div class="pi-detail-column">
              <div class="compare-subtitle after-text">AFTER</div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">PI No:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.pi_no !== amendmentData.pi_after?.[piIndex - 1]?.pi_no ? 'diff-after' : ''">
                  {{ amendmentData.pi_after?.[piIndex - 1]?.pi_no || "N/A" }}
                </span>
              </div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">PI Date:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.pi_date !== amendmentData.pi_after?.[piIndex - 1]?.pi_date ? 'diff-after' : ''">
                  {{ amendmentData.pi_after?.[piIndex - 1]?.pi_date ? formatDateView(amendmentData.pi_after[piIndex - 1].pi_date) : "N/A" }}
                </span>
              </div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">Validity:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.validity !== amendmentData.pi_after?.[piIndex - 1]?.validity ? 'diff-after' : ''">
                  {{ amendmentData.pi_after?.[piIndex - 1]?.validity ? formatDateView(amendmentData.pi_after[piIndex - 1].validity) : "N/A" }}
                </span>
              </div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">Approval Status:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.approval_status !== amendmentData.pi_after?.[piIndex - 1]?.approval_status ? 'diff-after' : ''">
                  {{ amendmentData.pi_after?.[piIndex - 1]?.approval_status || "N/A" }}
                </span>
              </div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">Sub Total:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.sub_total !== amendmentData.pi_after?.[piIndex - 1]?.sub_total ? 'diff-after' : ''">
                  {{ formatCurrency(amendmentData.pi_after?.[piIndex - 1]?.sub_total, amendmentData.pi_after?.[piIndex - 1]?.currency?.code) }}
                </span>
              </div>

              <div class="pi-detail-row">
                <span class="pi-detail-label">Total Amount:</span>
                <span class="pi-detail-value" :class="amendmentData.pi_before?.[piIndex - 1]?.total_amount !== amendmentData.pi_after?.[piIndex - 1]?.total_amount ? 'diff-after' : ''">
                  {{ formatCurrency(amendmentData.pi_after?.[piIndex - 1]?.total_amount, amendmentData.pi_after?.[piIndex - 1]?.currency?.code) }}
                </span>
              </div>
            </div>
          </div>

          <div class="pi-item-wrap">
            <div class="pi-item-block">
              <div class="items-header before-header">Items Before</div>

              <table class="items-table">
                <thead>
                <tr>
                  <th style="width: 6%">SL</th>
                  <th style="width: 18%">Product</th>
                  <th style="width: 15%">HS Code</th>
                  <th style="width: 14%; text-align: right">Qty</th>
                  <th style="width: 15%; text-align: right">Shipped Qty</th>
                  <th style="width: 14%; text-align: right">Rate</th>
                  <th style="width: 18%; text-align: right">Amount</th>
                </tr>
                </thead>

                <tbody>
                <tr v-for="(item, itemIndex) in amendmentData.pi_before?.[piIndex - 1]?.details || []" :key="itemIndex">
                  <td>{{ itemIndex + 1 }}</td>
                  <td>{{ item.product_id ?? "N/A" }}</td>
                  <td>{{ item.hs_code || "N/A" }}</td>
                  <td class="text-right">{{ Number(item.qty || 0).toFixed(2) }}</td>
                  <td class="text-right">{{ item.shipped_qty ?? "0" }}</td>
                  <td class="text-right">
                    {{ formatCurrency(item.rate, amendmentData.pi_before?.[piIndex - 1]?.currency?.code) }}
                  </td>
                  <td class="text-right">
                    {{ formatCurrency(item.amount, amendmentData.pi_before?.[piIndex - 1]?.currency?.code) }}
                  </td>
                </tr>

                <tr v-if="!amendmentData.pi_before?.[piIndex - 1]?.details || amendmentData.pi_before[piIndex - 1].details.length === 0">
                  <td colspan="7" class="text-center text-grey">No items found</td>
                </tr>
                </tbody>
              </table>
            </div>

            <div class="pi-item-block">
              <div class="items-header after-header">Items After</div>

              <table class="items-table">
                <thead>
                <tr>
                  <th style="width: 6%">SL</th>
                  <th style="width: 18%">Product</th>
                  <th style="width: 15%">HS Code</th>
                  <th style="width: 14%; text-align: right">Qty</th>
                  <th style="width: 15%; text-align: right">Shipped Qty</th>
                  <th style="width: 14%; text-align: right">Rate</th>
                  <th style="width: 18%; text-align: right">Amount</th>
                </tr>
                </thead>

                <tbody>
                <tr v-for="(item, itemIndex) in amendmentData.pi_after?.[piIndex - 1]?.details || []" :key="itemIndex">
                  <td>{{ itemIndex + 1 }}</td>
                  <td>{{ item.product_id ?? "N/A" }}</td>
                  <td>{{ item.hs_code || "N/A" }}</td>
                  <td class="text-right">{{ Number(item.qty || 0).toFixed(2) }}</td>
                  <td class="text-right">{{ item.shipped_qty ?? "0" }}</td>
                  <td class="text-right">
                    {{ formatCurrency(item.rate, amendmentData.pi_after?.[piIndex - 1]?.currency?.code) }}
                  </td>
                  <td class="text-right">
                    {{ formatCurrency(item.amount, amendmentData.pi_after?.[piIndex - 1]?.currency?.code) }}
                  </td>
                </tr>

                <tr v-if="!amendmentData.pi_after?.[piIndex - 1]?.details || amendmentData.pi_after[piIndex - 1].details.length === 0">
                  <td colspan="7" class="text-center text-grey">No items found</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="summary-row">
            <table class="summary-table before-summary">
              <tbody>
              <tr>
                <td><strong>Sub Total (Before)</strong></td>
                <td class="text-right">
                  {{ formatCurrency(amendmentData.pi_before?.[piIndex - 1]?.sub_total, amendmentData.pi_before?.[piIndex - 1]?.currency?.code) }}
                </td>
              </tr>
              </tbody>
            </table>

            <table class="summary-table after-summary">
              <tbody>
              <tr>
                <td><strong>Sub Total (After)</strong></td>
                <td class="text-right">
                  {{ formatCurrency(amendmentData.pi_after?.[piIndex - 1]?.sub_total, amendmentData.pi_after?.[piIndex - 1]?.currency?.code) }}
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-else class="pi-section">
        <div class="section-title">Proforma Invoice Comparison</div>
        <p class="empty-message">No Proforma Invoice data associated with this amendment</p>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import { formatDateView } from "@/plugins/helpers";

export default {
  name: "lcAmendmentDetailsTable",

  data() {
    return {
      loading: false,
      amendmentData: {},
    };
  },

  methods: {
    formatDateView,

    formatCurrency(amount, currencyCode = null) {
      if (amount === null || amount === undefined || amount === "") {
        return "0.00";
      }

      const formatted = Number(amount).toFixed(2);
      const currency = currencyCode || this.amendmentData?.lc_after?.currency?.code || this.amendmentData?.lc_before?.currency?.code || "USD";

      return `${currency} ${formatted}`;
    },

    async loadLcData(id) {
      if (!id) {
        toast.error("No LC Amendment ID provided");
        return;
      }

      this.loading = true;

      this.$store
          .dispatch("getLcImportLcAmendmentById", id)
          .then((res) => {
            this.amendmentData = res?.data ?? res ?? {};
          })
          .catch((error) => {
            toast.error(error.response?.data?.message || "Failed to load LC amendment data");
          })
          .finally(() => {
            this.loading = false;
          });
    },
  },

  mounted() {
    const id = this.$route.query.referenceable_id;
    this.loadLcData(id);
  },
};
</script>

<style scoped>
.section-title {
  background: #f2f2f2;
  padding: 8px;
  margin-top: 20px;
  font-weight: bold;
  border-left: 4px solid #555;
  color: #333;
}

.info-table,
.items-table,
.compare-table,
.summary-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  font-size: 14px;
}

.info-table td,
.items-table th,
.items-table td,
.compare-table td,
.compare-table th,
.summary-table td {
  border: 1px solid #ddd;
  padding: 8px;
}

.info-table .info-label {
  width: 25%;
  background-color: #f5f5f5;
  font-weight: normal;
}

.info-table .info-value {
  width: 25%;
}

.chip {
  display: inline-block;
  background: #607d8b;
  color: #fff;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  margin-right: 4px;
}

.compare-wrap {
  display: flex;
  gap: 20px;
  margin-top: 10px;
}

.compare-column {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.compare-header {
  color: #fff;
  padding: 10px 14px;
  font-weight: bold;
  font-size: 14px;
}

.before-header {
  background-color: #b0413e;
}

.after-header {
  background-color: #2e7d32;
}

.compare-label {
  width: 45%;
  background: #faf5f5;
  font-weight: 600;
}

.after-column .compare-label {
  background: #f5faf6;
}

.diff-before {
  background: #fff1f0;
  color: #b71c1c;
}

.diff-after {
  background: #f0fff2;
  color: #1b5e20;
  font-weight: bold;
}

.pi-section {
  margin-top: 20px;
  page-break-inside: avoid;
}

.pi-header-bar {
  background-color: #607d8b;
  color: white;
  padding: 12px 16px;
  border-radius: 4px 4px 0 0;
  display: flex;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
}

.pi-details {
  display: flex;
  gap: 20px;
  background-color: white;
  border: 1px solid #ddd;
  border-top: none;
  padding: 16px;
}

.pi-detail-column {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.compare-subtitle {
  font-weight: bold;
  margin-bottom: 8px;
  font-size: 13px;
}

.before-text {
  color: #b71c1c;
}

.after-text {
  color: #1b5e20;
}

.pi-detail-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
}

.pi-detail-label {
  color: #555;
}

.pi-detail-value {
  color: #333;
}

.pi-item-wrap {
  display: flex;
  gap: 20px;
  margin-top: 15px;
}

.pi-item-block {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.items-header {
  color: #fff;
  padding: 8px 10px;
  font-weight: bold;
  font-size: 13px;
}

.items-table th {
  background: #eee;
  text-align: left;
}

.summary-row {
  display: flex;
  gap: 20px;
  margin-top: 15px;
}

.summary-table {
  flex: 1;
}

.before-summary td:first-child {
  background: #faf5f5;
}

.after-summary td:first-child {
  background: #f5faf6;
}

.empty-message {
  text-align: center;
  color: #999;
  padding: 16px;
}

.signature-area {
  margin-top: 48px;
  display: flex;
  justify-content: space-between;
  gap: 20px;
  page-break-inside: avoid;
}

.signature-box {
  width: 220px;
  text-align: center;
  font-size: 14px;
}

.signature-line {
  border-top: 1px solid #000;
  margin-top: 40px;
  padding-top: 5px;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.text-grey {
  color: #6b7280;
}

@media (max-width: 960px) {
  .compare-wrap,
  .pi-details,
  .pi-item-wrap,
  .summary-row,
  .signature-area {
    flex-direction: column;
  }
}

</style>